import { cn } from '../../lib/cn.ts';
import { formatPercent } from '../../lib/format.ts';
import { DATA_SLOT_BG } from './PulseChart.tsx';
import type { DataSlot } from './PulseChart.tsx';

export interface ShareBarPart {
  key: string;
  label: string;
  /** Share of the whole, 0 to 1. */
  value: number;
  /** Chart slot, in order; parts keep their slot across the page. */
  slot?: DataSlot;
  /** Folded or unexplained remainder: grey, never a series colour. */
  other?: boolean;
}

export interface ShareBarProps {
  /** Up to 5 parts (data-viz.md); what they leave of the whole stays an empty track. */
  parts: readonly ShareBarPart[];
  /** Accessible summary: "CPU of devbox: heavy work 41%, background 20%, idle 39%". */
  label: string;
  format?: (fraction: number) => string;
  /** Hide the legend when the parts are named next to the bar already. */
  legend?: boolean;
  className?: string;
}

/**
 * One whole as a single stacked bar with its parts labelled under it (data-viz.md: what share
 * of a whole, 5 parts or fewer). Parts sit left to right in slot colours with 2 px between them;
 * the rest of the track is what nobody uses. Static: a new reading replaces the widths, nothing
 * grows or loops.
 */
export function ShareBar({ parts, label, format = (n) => formatPercent(n), legend = true, className }: ShareBarProps) {
  const shown = parts.filter((p) => p.value > 0);
  let left = 1;
  return (
    <div className={cn('flex min-w-0 flex-col gap-2', className)}>
      <span role="img" aria-label={label} className="flex h-2 min-w-0 gap-0.5 overflow-hidden rounded-pill bg-active">
        {shown.map((p) => {
          const w = Math.max(0, Math.min(left, p.value));
          left -= w;
          return <span key={p.key} data-part={p.key} className={cn('h-full shrink-0', p.other ? 'bg-fg-subtle' : DATA_SLOT_BG[p.slot ?? 1])} style={{ width: `${w * 100}%` }} />;
        })}
      </span>
      {legend && (
        <ul aria-hidden className="flex min-w-0 flex-wrap gap-x-4 gap-y-1">
          {parts.map((p) => (
            <li key={p.key} className="inline-flex min-w-0 items-center gap-1.5 text-caption text-fg-subtle">
              <span className={cn('size-2 shrink-0 rounded-pill', p.other ? 'bg-fg-subtle' : DATA_SLOT_BG[p.slot ?? 1])} />
              <span className="truncate">{p.label}</span>
              <span className="text-fg-muted num">{format(p.value)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
